import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useKleurenOrderDetail, useAddNote } from "@/hooks/useKleurenOrders";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Send } from "lucide-react";

interface KleurenOrderDetailSheetProps {
  ordernumber: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const KleurenOrderDetailSheet = ({ ordernumber, open, onOpenChange }: KleurenOrderDetailSheetProps) => {
  const { data: detail, isLoading } = useKleurenOrderDetail(ordernumber);
  const addNote = useAddNote();
  const { user } = useAuth();
  const [noteMessage, setNoteMessage] = useState("");

  const handleSendNote = () => {
    if (!ordernumber || !noteMessage.trim()) return;
    addNote.mutate(
      { ordernumber, message: noteMessage.trim(), sender_name: user?.name },
      { onSuccess: () => setNoteMessage("") }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Order {ordernumber}</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : detail ? (
          <div className="space-y-6 mt-6">
            {/* Klant info */}
            {detail.customer && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Klant</h3>
                <p className="text-sm">{detail.customer.firstname} {detail.customer.lastname}</p>
              </div>
            )}

            {/* Line items */}
            {detail.line_items && detail.line_items.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Producten</h3>
                <div className="space-y-2">
                  {detail.line_items.map((item: any, i: number) => (
                    <div key={i} className="text-sm p-2 bg-muted/50 rounded">
                      <div className="font-medium">{item.producttitle}</div>
                      {item.customfieldcolor && (
                        <div className="text-primary font-semibold">{item.customfieldcolor}</div>
                      )}
                      <div className="text-muted-foreground text-xs">
                        {item.brand_title} · {item.sku} · Aantal: {item.quantityordered}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Print history */}
            {detail.print_history && detail.print_history.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Afdruk historie</h3>
                <div className="space-y-1">
                  {detail.print_history.map((ph: any, i: number) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      {ph.printed_at} – {ph.printed_by || 'Onbekend'}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notitie toevoegen */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Notitie toevoegen</h3>
              <div className="flex gap-2">
                <Textarea
                  placeholder="Bijv. Kleur nagekeken, OK"
                  value={noteMessage}
                  onChange={(e) => setNoteMessage(e.target.value)}
                  className="min-h-[60px]"
                />
                <Button
                  size="icon"
                  onClick={handleSendNote}
                  disabled={!noteMessage.trim() || addNote.isPending}
                  className="shrink-0 self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Geen details gevonden
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default KleurenOrderDetailSheet;
